import { createClient } from 'contentful';
import env from '../env.js';
import { withHttpsAssetUrls } from './contentful-asset-urls.js';
import { withCmsFixture } from './contentful-fixture.js';
import { wrapClientWithLocalFixtures } from './local-page-fixtures.js';

// Per-space credentials, shared by the module-level singletons and by any
// per-request clients the factory builds.
const SPACE_CREDENTIALS = {
  marketing: {
    accessToken: env.CONTENTFUL_ACCESS_TOKEN,
    space: env.CONTENTFUL_SPACE_ID, // plaid.com space (marketing pages)
  },
  partner: {
    accessToken: env.PARTNER_ACCESS_TOKEN,
    space: env.PARTNER_SPACE_ID, // partner directory space
  },
  blog: {
    accessToken: env.BLOG_ACCESS_TOKEN,
    space: env.BLOG_SPACE_ID, // blog space
  },
};

// contentful.js v10+: removeUnresolved config was replaced by the
// withoutUnresolvableLinks client chain modifier.
//
// Happo builds wrap the finished client in the record/replay fixture layer
// (src/lib/contentful-fixture.js) so captures render a committed content
// snapshot; outside those builds the wrapper is a no-op.
const createCmsClient = ({
  accessToken,
  space,
  spaceName,
  timelinePreview,
}) => {
  return withHttpsAssetUrls(
    withCmsFixture(
      createClient({
        accessToken,
        environment: env.CONTENTFUL_ENVIRONMENT,
        host: env.CONTENTFUL_HOST,
        space,
        retryLimit: 50,
        // Only set the key when scoping to a release; passing it unconditionally
        // would opt every client into Timeline validation.
        ...(timelinePreview && { timelinePreview }),
      }).withoutUnresolvableLinks,
      spaceName,
    ),
    { rewriteImageHosts: process.env.HAPPO_PAGES_FIXTURES === 'true' },
  );
};

/**
 * Build one CMS client per Contentful space.
 *
 * Timeline Preview scopes every request to a single release, so those clients
 * have to be built per request: setting a release on the module-level
 * singletons would leak one editor's release into unrelated traffic.
 *
 * @param {object} [options]
 * @param {{ release?: { lte: string }, timestamp?: { lte: string | Date } }} [options.timelinePreview]
 *   Release and/or timestamp to scope requests to. Omit for normal delivery
 *   and normal (non-Timeline) preview.
 * @returns {{ marketingCmsClient: object, partnerCmsClient: object, blogCmsClient: object }}
 */
export const createCmsClients = ({ timelinePreview } = {}) => {
  // Timeline Preview is only served by the Content Preview API. contentful.js
  // rewrites the request path to `timeline/<path>` whenever a `timelinePreview`
  // config is present, so a misconfigured host fails loudly rather than quietly
  // serving published content. Measured behavior per host:
  //
  //   Host                         | SDK behavior                   | Resulting request
  //   -----------------------------|--------------------------------|-------------------------------------------------
  //   (unset)                      | allowed; host is not a string  | cdn.contentful.com/../timeline/entries -> 401
  //   cdn.contentful.com           | throws before any request      | none
  //   preview.contentful.com       | works                          | preview.contentful.com/../timeline/entries
  //   contentful-proxy.example.com | allowed; custom-host opt-out   | contentful-proxy.example.com/../timeline/entries
  //
  // Only the unset case is guarded here. Its 401 blames the access token when
  // the real problem is the host, which is the one genuinely misleading
  // failure; the SDK's own error already covers cdn.contentful.com.
  //
  // Alternatives considered, reviewers welcome to disagree:
  //   1. No guard. Every case above already fails loudly, just less clearly.
  //   2. Require an exact `preview(.eu).contentful.com` host. Clearest failure,
  //      but rejects the custom-host opt-out Contentful supports on purpose for
  //      proxies it cannot identify as CPA.
  //
  // @see https://www.contentful.com/developers/docs/references/content-preview-api/
  if (timelinePreview && !env.CONTENTFUL_HOST) {
    throw new Error(
      'Timeline Preview requires the Content Preview API, but CONTENTFUL_HOST is unset, ' +
        'so requests would go to the Delivery API. Set it to preview.contentful.com to preview a release.',
    );
  }

  return {
    marketingCmsClient: createCmsClient({
      ...SPACE_CREDENTIALS.marketing,
      spaceName: 'marketing',
      timelinePreview,
    }),
    partnerCmsClient: createCmsClient({
      ...SPACE_CREDENTIALS.partner,
      spaceName: 'partner',
      timelinePreview,
    }),
    blogCmsClient: createCmsClient({
      ...SPACE_CREDENTIALS.blog,
      spaceName: 'blog',
      timelinePreview,
    }),
  };
};

// Default singletons: production delivery, static builds, and normal preview.
//
// The marketing client carries a second, unrelated fixture layer: local page
// authoring (src/lib/local-page-fixtures.js) serves `.local-pages/` ahead of
// the API. It gates on `isDevelopment`, where the Happo layer above gates on
// HAPPO_PAGES_FIXTURES, so the two never engage in the same run. It sits
// outside so a local fixture wins over a live fetch in dev.
//
// Per-request clients from createCmsClients() are intentionally left unwrapped
// so Timeline Preview is never masked by local fixtures.
const defaultCmsClients = createCmsClients();

export const marketingCmsClient = wrapClientWithLocalFixtures(
  defaultCmsClients.marketingCmsClient,
);
export const { partnerCmsClient, blogCmsClient } = defaultCmsClients;
